// @ts-nocheck
import { forwardRef, useMemo } from 'react';
import {
  Box,
  Stack,
  Text,
  useMantineTheme,
  useComputedColorScheme,
} from '@mantine/core';
import { Paper } from '../../../ui';
import { FadeIn } from '../../../animations';
import { pickTextColorForBackground } from '../../../../utils/color';
import type { GaugeProps } from '../types';

/**
 * SemiCircleGauge - Semi-circle gauge
 *
 * Displays progress as a semi-circle arc, similar to speedometer.
 */
const SemiCircleGauge = forwardRef<HTMLDivElement, GaugeProps>(
  (
    {
      title,
      value,
      max,
      min = 0,
      unit,
      description,
      thresholds,
      showPercentage = true,
      showValue = true,
      size = 'md',
      variant = 'default',
      color,
      className,
    },
    ref
  ) => {
    const theme = useMantineTheme();
    const colorScheme = useComputedColorScheme('light');
    const isDark = colorScheme === 'dark';

    const sizes = {
      xs: { width: 120, height: 60, thickness: 8, fontSize: 'sm' },
      sm: { width: 160, height: 80, thickness: 10, fontSize: 'md' },
      md: { width: 200, height: 100, thickness: 12, fontSize: 'lg' },
      lg: { width: 240, height: 120, thickness: 14, fontSize: 'xl' },
      xl: { width: 280, height: 140, thickness: 16, fontSize: '2rem' },
    };

    const currentSize = sizes[size];

    const percentage = Math.min(Math.max(((value - min) / (max - min)) * 100, 0), 100);

    const getGaugeColor = () => {
      if (color) return color;

      if (thresholds && thresholds.length > 0) {
        const sortedThresholds = [...thresholds].sort((a, b) => b.value - a.value);
        for (const threshold of sortedThresholds) {
          if (value >= threshold.value) {
            return threshold.color;
          }
        }
        return sortedThresholds[sortedThresholds.length - 1].color;
      }

      if (percentage >= 70) return theme.colors.green[6];
      if (percentage >= 30) return theme.colors.yellow[6];
      return theme.colors.red[6];
    };

    const gaugeColor = getGaugeColor();

    // Calculate text color based on Paper background
    const textColor = useMemo(() => {
      if (variant === 'gradient') {
        // If using gradient variant on Paper, calculate text color
        // For now, we'll use the default Paper background which varies by colorScheme
        return pickTextColorForBackground(isDark ? theme.colors.dark[7] : theme.white);
      }
      // For default/outlined variants, use theme defaults
      return isDark ? theme.white : theme.black;
    }, [variant, isDark, theme]);

    // SVG dimensions
    const centerX = currentSize.width / 2;
    const centerY = currentSize.height - 10;
    const radius = Math.min(currentSize.width, currentSize.height * 2) / 2 - currentSize.thickness;

    // Calculate arc path
    const startAngle = 180;
    const endAngle = 0;
    const angle = startAngle + (endAngle - startAngle) * (percentage / 100);

    const getArcPath = (startDeg: number, endDeg: number, r: number) => {
      const start = ((startDeg - 90) * Math.PI) / 180;
      const end = ((endDeg - 90) * Math.PI) / 180;

      const x1 = centerX + r * Math.cos(start);
      const y1 = centerY + r * Math.sin(start);
      const x2 = centerX + r * Math.cos(end);
      const y2 = centerY + r * Math.sin(end);

      const largeArc = endDeg - startDeg > 180 ? 1 : 0;

      return `M ${x1} ${y1} A ${r} ${r} 0 ${largeArc} 1 ${x2} ${y2}`;
    };

    return (
      <FadeIn>
        <Paper ref={ref} variant={variant} p="lg" className={className}>
          <Stack gap="md" align="center">
            {title && (
              <Text size="sm" fw={600} ta="center">
                {title}
              </Text>
            )}

            <Box style={{ position: 'relative', width: currentSize.width, height: currentSize.height }}>
              <svg width={currentSize.width} height={currentSize.height}>
                {/* Background arc */}
                <path
                  d={getArcPath(180, 0, radius)}
                  fill="none"
                  stroke={isDark ? theme.colors.dark[4] : theme.colors.gray[2]}
                  strokeWidth={currentSize.thickness}
                  strokeLinecap="round"
                />

                {/* Progress arc */}
                <path
                  d={getArcPath(180, angle, radius)}
                  fill="none"
                  stroke={gaugeColor}
                  strokeWidth={currentSize.thickness}
                  strokeLinecap="round"
                  style={{
                    transition: 'all 1s ease-out',
                  }}
                />

                {/* Center text */}
                <text
                  x={centerX}
                  y={centerY - 10}
                  textAnchor="middle"
                  fontSize={currentSize.fontSize}
                  fontWeight="700"
                  fill={textColor}
                >
                  {showValue && `${value}${unit || ''}`}
                </text>

                {showPercentage && (
                  <text
                    x={centerX}
                    y={centerY + 10}
                    textAnchor="middle"
                    fontSize="12px"
                    fill={theme.colors.gray[6]}
                  >
                    {percentage.toFixed(1)}%
                  </text>
                )}
              </svg>
            </Box>

            {description && (
              <Text size="xs" c="dimmed" ta="center">
                {description}
              </Text>
            )}
          </Stack>
        </Paper>
      </FadeIn>
    );
  }
);

SemiCircleGauge.displayName = 'SemiCircleGauge';

export default SemiCircleGauge;
